import React, { useState, useEffect, useRef } from 'react';
import Store3DView from './Store3DView';
import StoreView2D from './storeview2d';
import Warehouse3DManager from './Warehouse3DManager';
import Warehouse3DSimple from './Warehouse3DSimple';
import { warehouseService } from '../../_services/warehouse.service';

const colorMap = {
  RED: '#d32f2f',
  YELLOW: '#ffeb3b',
  GREEN: '#388e3c',
  GRAY: '#90a4ae',
  ORANGE: '#ff9800',
};

function getBinColor(bin) {
  return colorMap[bin.color] || '#90a4ae';
}

const RealWarehouse2DView = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [racks, setRacks] = useState([]);
  const [selectedRack, setSelectedRack] = useState('');
  const [bins, setBins] = useState([]);
  const [selectedBin, setSelectedBin] = useState('');
  const [warehouseDetails, setWarehouseDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch warehouse suggestions on mount
  useEffect(() => {
    warehouseService.getSuggestion().then(res => {
      setWarehouses(res.content || []);
    });
  }, []);

  // Fetch racks when warehouse changes
  useEffect(() => {
    if (selectedWarehouse) {
      warehouseService.getRacksByWarehouseId(selectedWarehouse).then(res => {
        setRacks(res.content || []);
      });
    } else {
      setRacks([]);
    }
    setSelectedRack('');
    setSelectedBin('');
    setBins([]);
  }, [selectedWarehouse]);

  // Fetch bins when rack changes
  useEffect(() => {
    if (selectedRack) {
      const rack = racks.find(r => r.id === selectedRack);
      setBins((rack && rack.stockBins) || []);
    } else {
      setBins([]);
    }
    setSelectedBin('');
  }, [selectedRack, racks]);

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await warehouseService.fetchWarehouseData(
        selectedWarehouse || undefined,
        selectedRack || undefined,
        selectedBin || undefined
      );
      if (data.content && data.content[0]) {
        setWarehouseDetails(data.content[0]);
      } else {
        setWarehouseDetails(null);
        setError('No data found');
      }
    } catch (e) {
      setError('Failed to fetch warehouse data');
      setWarehouseDetails(null);
    }
    setLoading(false);
  };

  // Prepare rows for 2D grid
  let rows = [];
  if (warehouseDetails && warehouseDetails.racks) {
    warehouseDetails.racks.forEach((rack, idx) => {
      rows.push({ type: 'rack', rack });
      if ((idx + 1) % 2 === 0 && idx !== warehouseDetails.racks.length - 1) {
        rows.push({ type: 'pathway' });
      }
    });
  }

  return (
    <div style={{ padding: 24, background: '#10162a', borderRadius: 16, minHeight: 400, overflowX: 'auto', fontFamily: 'Inter, Arial, sans-serif' }}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'center' }}>
        <select value={selectedWarehouse} onChange={e => setSelectedWarehouse(e.target.value)} style={{ padding: 8, borderRadius: 6 }}>
          <option value=''>Select Warehouse</option>
          {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select value={selectedRack} onChange={e => setSelectedRack(e.target.value)} style={{ padding: 8, borderRadius: 6 }} disabled={!selectedWarehouse}>
          <option value=''>Select Rack</option>
          {racks.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select value={selectedBin} onChange={e => setSelectedBin(e.target.value)} style={{ padding: 8, borderRadius: 6 }} disabled={!selectedRack}>
          <option value=''>Select Bin</option>
          {bins.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <button onClick={handleSearch} style={{ padding: '8px 20px', borderRadius: 6, background: '#388e3c', color: '#fff', fontWeight: 700, border: 'none', cursor: 'pointer' }}>Search</button>
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 16, alignItems: 'center' }}>
        <span><span style={{ background: '#d32f2f', width: 16, height: 16, display: 'inline-block', borderRadius: 4, marginRight: 6 }} /> <span style={{color:'#fff'}}>Damage</span></span>
        <span><span style={{ background: '#ffeb3b', width: 16, height: 16, display: 'inline-block', borderRadius: 4, marginRight: 6 }} /> <span style={{color:'#fff'}}>Verification</span></span>
        <span><span style={{ background: '#388e3c', width: 16, height: 16, display: 'inline-block', borderRadius: 4, marginRight: 6 }} /> <span style={{color:'#fff'}}>Complete</span></span>
        <span><span style={{ background: '#90a4ae', width: 16, height: 16, display: 'inline-block', borderRadius: 4, marginRight: 6 }} /> <span style={{color:'#fff'}}>MT</span></span>
        <span><span style={{ background: '#ff9800', width: 16, height: 16, display: 'inline-block', borderRadius: 4, marginRight: 6 }} /> <span style={{color:'#fff'}}>Warning</span></span>
      </div>
      {/* Error/Loading */}
      {error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}
      {loading && <div style={{ color: '#fff', marginBottom: 16 }}>Loading...</div>}
      {/* 2D Grid */}
      {warehouseDetails && warehouseDetails.racks && warehouseDetails.racks.length > 0 && rows.map((row, i) => {
        if (row.type === 'pathway') {
          return (
            <div key={`pathway-${i}`} style={{
              display: 'flex',
              alignItems: 'center',
              margin: '12px 0',
              justifyContent: 'center',
            }}>
              <div style={{
                background: '#444',
                color: '#fff',
                borderRadius: 8,
                padding: '8px 48px',
                fontWeight: 700,
                fontSize: 18,
                letterSpacing: 2,
                opacity: 0.7,
                textAlign: 'center',
                minWidth: 200,
              }}>Pathway</div>
            </div>
          );
        }
        const { rack } = row;
        return (
          <div key={rack.id} style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 10,
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 8,
            padding: '8px 0',
          }}>
            {/* Rack label */}
            <div style={{
              minWidth: 90,
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              marginRight: 16,
              background: '#23263a',
              borderRadius: 6,
              padding: '8px 16px',
              textAlign: 'center',
              letterSpacing: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div>{rack.name}</div>
              <div style={{fontSize:12, fontWeight:400, opacity:0.7}}>{rack.code}</div>
            </div>
            {/* Bins */}
            <div style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              flex: 1,
            }}>
              {(rack.stockBins || []).map(bin => (
                <div
                  key={bin.id}
                  style={{
                    minWidth: 70,
                    height: 48,
                    background: getBinColor(bin),
                    color: bin.color === 'YELLOW' ? '#222' : '#fff',
                    borderRadius: 6,
                    fontWeight: 700,
                    fontSize: 13,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #222',
                    letterSpacing: 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    cursor: 'pointer',
                    transition: 'transform 0.1s',
                    marginRight: 0,
                  }}
                >
                  <span>{bin.name}</span>
                  <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>{bin.availableStockQuantity} / {bin.allowedStockQuantity}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Warehouse2DViewSwitcher = () => {
  const [tab, setTab] = useState('2d');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef();

  const handleFullscreenToggle = () => {
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.mozRequestFullScreen) {
        containerRef.current.mozRequestFullScreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        padding: 32,
        position: 'relative',
        background: isFullscreen ? '#10162a' : undefined,
        width: isFullscreen ? '100vw' : undefined,
        height: isFullscreen ? '100vh' : undefined,
        overflow: isFullscreen ? 'auto' : undefined,
        maxWidth: isFullscreen ? undefined : '',
        margin: isFullscreen ? 0 : '',
      }}
    >
      <div style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
        <button onClick={() => setTab('2d')} style={{ padding: '10px 32px', borderRadius: 8, fontWeight: 700, fontSize: 16, background: tab === '2d' ? '#388e3c' : '#23263a', color: '#fff', border: 'none', cursor: 'pointer', boxShadow: tab === '2d' ? '0 2px 8px #388e3c44' : 'none' }}>2D View</button>
        <button onClick={() => setTab('3d')} style={{ padding: '10px 32px', borderRadius: 8, fontWeight: 700, fontSize: 16, background: tab === '3d' ? '#388e3c' : '#23263a', color: '#fff', border: 'none', cursor: 'pointer', boxShadow: tab === '3d' ? '0 2px 8px #388e3c44' : 'none' }}>3D View</button>
        <button
          onClick={handleFullscreenToggle}
          style={{
            marginLeft: 'auto',
            padding: '10px 28px',
            borderRadius: 8,
            background: isFullscreen ? 'linear-gradient(135deg, #f87171, #ef4444)' : 'linear-gradient(135deg, #10b981, #059669)',
            color: '#fff',
            fontWeight: 700,
            border: 'none',
            cursor: 'pointer',
            fontSize: 16,
            boxShadow: '0 2px 8px #388e3c44',
            position: 'absolute',
            top: 24,
            right: 24,
            zIndex: 10,
          }}
          title={isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
        >
          {isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
        </button>
      </div>
      {tab === '2d' ? <StoreView2D onViewChange={() => setTab('3d')} /> : <Warehouse3DSimple />}
    </div>
  );
};

export default Warehouse2DViewSwitcher; 